/**
 * A1 (2026-05-26 post-demo) — Strip reviewer-instruction patterns
 * that bleed from review-loop context into generated content.
 *
 * Complements MEL (`narrativeHygiene.stripMetaInstructions`) at the
 * persistence boundary with SWC-specific patterns and a full-payload walk.
 */
import type { SiteRagReport } from '../types/siteRag';
import { PUBLIC_SENTIMENT_META_V2_KEYS } from './sentiment/publicSentimentMetaV2Contract';

/** The deterministic `psr-meta-v2` projection quotes source excerpts verbatim
 * and is covered by the deterministic-screening content hash; reviewer-voice
 * patterns (e.g. "This section … must …") can match ordinary council minutes.
 * These paths are data, never model narrative, so they are never rewritten. */
const SOURCE_PROJECTION_PATHS: ReadonlySet<string> = new Set(
  PUBLIC_SENTIMENT_META_V2_KEYS.map(key => `publicSentimentMeta.${key}`),
);

const META_INSTRUCTION_PATTERNS: RegExp[] = [
  /^.{0,80}must (highlight|emphasize|include|note|mention|address|reflect|incorporate).+?(See correctedSections|See correctedReports?).*?for (full text|details?)\.?$/im,
  /^\s*See correctedSections for (full text|details?)\.?\s*$/im,
  /^.{0,80}(The Executive Summary|This section|This report) (and a new .+? section )?must .+$/im,
  /^(AGC Constructability Review \+ Mortenson \/ Turner Practice|RSMeans \+ Mortenson \/ Turner Programme Benchmarks|JLL Benchmark Set|Cushman & Wakefield Benchmark)\s*$/im,
  /^Data not available for this section\.?\s*$/im,
  /^.*?\bcorrectedSections\b.*?$/im,
  /^\s*\{\{[\w_.]+\}\}\s*$/m,
];

/**
 * P3-17 — the unavailability placeholder the prompts prescribe ("Data not
 * available — site-specific assessment required", often rendered with a comma
 * once em dashes are normalised). A field that is exactly this sentence is an
 * honest unavailability marker and is kept. The sentence must END after
 * "required" (terminal punctuation, a line break, or a new capitalised
 * sentence). A colon or semicolon continues the placeholder's own sentence
 * ("… required: a dispersion model must be run"), and "… required for the gas
 * lateral; …" is a qualified statement: neither is ever cut.
 */
// Case-sensitive on purpose: under /i the capital-letter lookahead would also
// accept a lowercase continuation.
const LEADING_PLACEHOLDER_SENTENCE_RE =
  /^\s*[Dd]ata not available\s*(?:[—–-]+|[,;:])?\s*(?:deterministic\s+)?[Ss]ite-specific assessment (?:is\s+)?required(?:\s*[.!]\s*|[ \t]*\n\s*|[ \t]+(?=[A-Z]))/;

/** A markdown heading line starts a different section of the field. */
const MARKDOWN_HEADING_LINE_RE = /^[ \t]{0,3}#{1,6}(?:[ \t]|$)/m;
/** A citation right after the placeholder may belong to the placeholder. */
const LEADING_CITATION_RE = /^\s*\[Ref\s+\d+\]/i;
const CITATION_RE = /\[Ref\s+\d+\]/i;

/**
 * The remainder keeps the field's unavailability qualification in its own
 * words: "the calculations have not yet been performed", "unverified",
 * "to be determined", "pending", "a site-specific study is required".
 */
const REMAINDER_UNAVAILABILITY_RE = new RegExp([
  String.raw`\b(?:not|never)\s+(?:yet\s+)?(?:been\s+)?(?:available|performed|completed|conducted|done|determined|quantified|calculated|modell?ed|assessed|evaluated|estimated|verified|confirmed|established|known|provided|identified|prepared|submitted|issued|filed|initiated|started|undertaken)\b`,
  String.raw`\b\w+n['’]t\s+(?:yet\s+)?(?:been\s+)?(?:available|performed|completed|conducted|done|determined|quantified|calculated|modell?ed|assessed|evaluated|estimated|verified|confirmed|established|known|provided|identified|prepared|submitted|issued|filed|initiated|started|undertaken)\b`,
  String.raw`\b(?:has|have|had)(?:\s+not|n['’]t)\s+(?:yet\s+)?been\b`,
  String.raw`\b(?:unavailable|unverified|unconfirmed|undetermined|unquantified|unknown|pending|outstanding|tbd)\b`,
  String.raw`\bto\s+be\s+(?:determined|confirmed|verified|quantified|calculated|assessed|performed|completed|prepared)\b`,
  String.raw`\bremains?\s+(?:to\s+be|open|outstanding|unresolved|unquantified)\b`,
  String.raw`\b(?:assessment|study|analysis|modell?ing|calculations?|survey|evaluation|inventory)\s+(?:is\s+|are\s+|will\s+be\s+)?required\b`,
].join('|'), 'i');

/** Cited text about this project, not a generic benchmark. */
const PROJECT_REFERENCE_RE = /\b(?:the|this)\s+(?:site|project|campus|parcel|property|facility|development|proposed\s+\w+|engineering\s+scope|scope\s+of\s+work|scope|owner|developer|applicant|sponsor|study\s+area|teaser|term\s+sheet)\b/i;

/** At least three words of prose once citation markers are removed. */
function isSubstantiveText(text: string): boolean {
  const words = text
    .replace(/\[Ref\s+\d+\]/gi, ' ')
    .split(/[^A-Za-z0-9'’-]+/)
    .filter(word => /[A-Za-z]{2,}/.test(word));
  return words.length >= 3;
}

/**
 * The text after the placeholder may replace it only when the reader keeps
 * the same information: it still states that the item is unavailable or
 * pending, or it is cited project-specific text. A generic, uncited
 * benchmark ("Typical permits take 12–18 months") would otherwise become a
 * bare assertion in a site-specific field. Only the text up to the next
 * markdown heading counts, and a citation directly after the placeholder
 * (which may cite the placeholder itself) blocks the strip.
 */
function canReplacePlaceholder(rest: string): boolean {
  if (LEADING_CITATION_RE.test(rest)) return false;
  const heading = MARKDOWN_HEADING_LINE_RE.exec(rest);
  const segment = heading ? rest.slice(0, heading.index) : rest;
  if (!isSubstantiveText(segment)) return false;
  return REMAINDER_UNAVAILABILITY_RE.test(segment)
    || (CITATION_RE.test(segment) && PROJECT_REFERENCE_RE.test(segment));
}

/**
 * P3-17 — a field that OPENS with the placeholder sentence and then carries
 * substantive text is a merge artifact: a later pass appended real prose to
 * the placeholder instead of replacing it, and the reader sees
 * "Data not available, site-specific assessment required. The engineering
 * scope identifies … have not yet been performed". Only that leading sentence
 * is removed, and only when the text that follows can stand in for it
 * (`canReplacePlaceholder`); otherwise the field is unchanged.
 */
export function stripLeadingPlaceholderSentence(text: string): { text: string; stripped: string | null } {
  const match = LEADING_PLACEHOLDER_SENTENCE_RE.exec(text);
  if (!match) return { text, stripped: null };
  const rest = text.slice(match[0].length);
  if (!canReplacePlaceholder(rest)) return { text, stripped: null };
  const opening = rest.charAt(0);
  const capitalized = /[a-z]/.test(opening) ? opening.toUpperCase() + rest.slice(1) : rest;
  return { text: capitalized, stripped: match[0].trim() };
}

export interface StripResult {
  cleaned: string;
  strippedMatches: Array<{ pattern: string; matched: string }>;
}

export function stripMetaInstructions(text: string): StripResult {
  if (!text || typeof text !== 'string') return { cleaned: text ?? '', strippedMatches: [] };
  let cleaned = text;
  const strippedMatches: Array<{ pattern: string; matched: string }> = [];

  for (const re of META_INSTRUCTION_PATTERNS) {
    const globalRe = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
    const matches = cleaned.match(globalRe);
    if (!matches) continue;
    for (const m of matches) strippedMatches.push({ pattern: re.source, matched: m });
    cleaned = cleaned.replace(globalRe, '');
  }

  const placeholder = stripLeadingPlaceholderSentence(cleaned);
  if (placeholder.stripped) {
    strippedMatches.push({ pattern: LEADING_PLACEHOLDER_SENTENCE_RE.source, matched: placeholder.stripped });
    cleaned = placeholder.text;
  }

  cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n').trim();
  return { cleaned, strippedMatches };
}

export function stripReportPayload<T extends Record<string, unknown>>(
  payload: T,
): { cleaned: T; totalStripped: number; details: Array<{ path: string; matches: StripResult['strippedMatches'] }> } {
  const details: Array<{ path: string; matches: StripResult['strippedMatches'] }> = [];
  const cleaned = walkAndStrip(payload, '', details) as T;
  const totalStripped = details.reduce((sum, d) => sum + d.matches.length, 0);
  return { cleaned, totalStripped, details };
}

function walkAndStrip(
  node: unknown,
  path: string,
  details: Array<{ path: string; matches: StripResult['strippedMatches'] }>,
): unknown {
  // Historical source receipts preserve the original quotes and reference
  // identities. Only these two named audit buckets bypass narrative cleanup.
  if ((path === 'gridLedStandbyPlanningAtGen' || path === 'inactiveGenerationImpactAuthorityAtGen') || path === 'qaFindings.sourceRoleReview' || path === 'qaFindings.finalSourceReview') return node;
  if (SOURCE_PROJECTION_PATHS.has(path)) return node;
  if (typeof node === 'string') {
    const result = stripMetaInstructions(node);
    if (result.strippedMatches.length > 0) details.push({ path, matches: result.strippedMatches });
    return result.cleaned;
  }
  if (Array.isArray(node)) return node.map((item, i) => walkAndStrip(item, `${path}[${i}]`, details));
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      out[k] = walkAndStrip(v, path ? `${path}.${k}` : k, details);
    }
    return out;
  }
  return node;
}

/** Apply A1 strip to a report before persistence; stamps metadata when matches found. */
export function applyMetaInstructionStripToReport(report: SiteRagReport): SiteRagReport {
  const stripResult = stripReportPayload(report as unknown as Record<string, unknown>);
  if (stripResult.totalStripped === 0) return report;

  console.warn('[A1 strip] Removed meta-instruction patterns:', {
    reportId: report.id,
    reportType: report.reportType,
    totalStripped: stripResult.totalStripped,
    details: stripResult.details,
  });

  return {
    ...stripResult.cleaned,
    metadata: {
      ...(report.metadata ?? {}),
      metaInstructionStripped: stripResult.totalStripped,
      metaInstructionStripDetails: stripResult.details,
    },
  } as SiteRagReport;
}
